import React, { useState } from 'react';

import GetElementWidth from 'common/components/GetElementWidth';
import { articleToTrackingProps } from 'common/tracking/articleToTrackingProps';
import type { RecommendationItemArticle } from 'common/types/recommendation';
import { useWidthChange } from './hooks/handlers';
import { useSliderTitle } from './hooks/title';
import { useProductViewEventTracker } from './hooks/tracking';
import RecommendationsCarousel from './RecommendationsCarousel';
import type { RecommendationsSliderBaseProps, RecommendationsSliderConfig } from './types';

const recommendationArticleToTrackingProps = ({
  campaigns,
  brand,
  trackingId,
  ...article
}: RecommendationItemArticle) =>
  articleToTrackingProps({
    ...article,
    campaigns: campaigns || [],
    brand: brand || null,
    trackingId: trackingId || null,
  });

const getTransformedItems = (items: RecommendationsSliderBaseProps['items']) => {
  return items.map(item => ({
    ...item,
    article: {
      ...item.article,
      tracking: recommendationArticleToTrackingProps(item.article),
    },
    strategyId: item.strategyId,
  }));
};

const RecommendationsSliderStatic = ({
  type,
  title: initialTitle,
  titleId,
  items: initialItems,
  withWishlistButton,
  eventDataList,
  isLazy,
  withDiscountLabelBackButton,
  config: { widthConfig, slideWidth, slideGap, isObserved, gapConfig },
}: RecommendationsSliderBaseProps & {
  config: RecommendationsSliderConfig;
}) => {
  const items = getTransformedItems(initialItems);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const { shownSlides, handleWidthChange } = useWidthChange({
    itemsCount: items.length,
    slideWidth,
    slideGap,
  });
  const title = useSliderTitle(type, initialTitle);

  useProductViewEventTracker({
    type,
    items,
    activeSlideIndex,
    shownSlides,
    eventDataList,
    isObserved,
  });

  if (!items.length) {
    return null;
  }

  return (
    <GetElementWidth onWidthChange={handleWidthChange}>
      <RecommendationsCarousel
        slides={items}
        widthConfig={widthConfig}
        gapConfig={gapConfig}
        width={slideWidth}
        slideGap={slideGap}
        onAfterSlide={setActiveSlideIndex}
        type={type}
        withWishlistButton={withWishlistButton}
        eventDataList={eventDataList}
        title={title}
        titleId={titleId}
        isLazy={isLazy}
        withDiscountLabelBackButton={withDiscountLabelBackButton}
      />
    </GetElementWidth>
  );
};

export default RecommendationsSliderStatic;
